import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";

import {
  useAddClientMutation,
  useUpdateClientMutation,
} from "src/api/sales/clients";
import { useGetCities, useGetCountries } from "src/api/generic";
import { useGetTermsList } from "src/api/sales/invoices";

const requiredIfTaxRegistered = yup.string().when("is_registered_tax", {
  is: "yes",
  then: (schema) => schema.required("Required"),
  otherwise: (schema) => schema,
});

const schema = yup.object().shape({
  name: yup.string().required("Required"),
  phone: yup.string().required("Required"),
  email: yup.string(),
  start_balance: yup.string(),
  bank_account: yup.string(),
  country_id: yup.string(),
  city_id: yup.string(),
  is_registered_tax: yup.string().oneOf(["yes", "no"]).required("Required"),
  vat: requiredIfTaxRegistered,
  street: requiredIfTaxRegistered,
  building_number: requiredIfTaxRegistered,
  zip_code: requiredIfTaxRegistered,
  cib: yup.string(),
  agreements: yup.string(),
  terms: yup.array().of(yup.string()),
  configuration: yup.object({
    show_phone: yup.boolean(),
    show_zip_code: yup.boolean(),
    show_email: yup.boolean(),
    show_city_id: yup.boolean(),
    show_country_id: yup.boolean(),
  }),
});

interface FormInputs extends yup.InferType<typeof schema> {}

interface IProps {
  open: boolean;
  handleClose: () => void;
  itemToUpdate?: any;
  refetch: Function;
}

const AddEditClientPopup = (props: IProps) => {
  const { open, handleClose, itemToUpdate, refetch } = props;
  const { t } = useTranslation("purchasing");
  const { t: salesT } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");
  const { t: profileT } = useTranslation("profile");
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  // APIS
  const { mutate, status, error, isPending } = useAddClientMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateClientMutation();
  const { data: countriesData } = useGetCountries();
  const { data: citiesData } = useGetCities();
  const { data: termsData } = useGetTermsList();

  //React hook form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      city_id: "1",
      country_id: "1",
      email: "",
      start_balance: "",
      bank_account: "",
      vat: "",
      street: "",
      building_number: "",
      zip_code: "",
      cib: "",
      agreements: "",
      terms: [],
      is_registered_tax: "yes",
      configuration: {
        show_email: true,
        show_zip_code: false,
        show_city_id: false,
        show_country_id: true,
        show_phone: false,
      },
    },
  });
  const mode = itemToUpdate ? "edit" : "add";

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const payload = {
      ...data,
      start_balance: data.start_balance || 0,
      has_tax_number: data.is_registered_tax === "yes" ? 1 : 0,
      configuration: {
        show_name: true,
        show_email: data.configuration.show_email,
        show_bank_account: false,
        show_phone: data.configuration.show_phone,
        show_start_balance: false,
        show_vat: true,
        show_country_id: data.configuration.show_country_id,
        show_city_id: data.configuration.show_city_id,
        show_street: true,
        show_building_number: true,
        show_zip_code: data.configuration.show_zip_code,
        show_cib: false,
      },
    };
    if (mode === "add") {
      mutate(payload);
    } else {
      mutateUpdate({
        id: itemToUpdate.id,
        ...payload,
      });
    }
  };

  useEffect(() => {
    if (itemToUpdate) {
      reset({
        name: itemToUpdate.name || "",
        phone: itemToUpdate.phone || "",
        email: itemToUpdate.email || "",
        start_balance: itemToUpdate.start_balance || "",
        bank_account: itemToUpdate.bank_account || "",
        is_registered_tax: itemToUpdate.has_tax_number ? "yes" : "no",
        vat: itemToUpdate.vat || "",
        country_id: itemToUpdate.country?.id || "",
        city_id: itemToUpdate.city?.id || "",
        street: itemToUpdate.street || "",
        building_number: itemToUpdate.building_number || "",
        zip_code: itemToUpdate.zip_code || "",
        cib: itemToUpdate.cib || "",
        agreements: itemToUpdate.agreements || "",
        terms: itemToUpdate.terms?.map((term: any) => term.id) || [],

        configuration: {
          show_email: itemToUpdate?.configuration?.show_email ?? false,
          show_zip_code: itemToUpdate?.configuration?.show_zip_code ?? false,
          show_city_id: itemToUpdate?.configuration?.show_city_id ?? false,
          show_country_id:
            itemToUpdate?.configuration?.show_country_id ?? false,
          show_phone: itemToUpdate?.configuration?.show_phone ?? false,
        },
      });
    }
  }, [itemToUpdate]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  console.log({ itemToUpdate }, getValues());

  return (
    <Modal
      open={open}
      // onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          minWidth: { xs: "100%", md: "537px" },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,

          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"} lineHeight={"24px"}>
            {mode === "add" ? salesT("addClient") : salesT("editClient")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {/* name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  type="string"
                  fullWidth
                  label={t("name")}
                  size="small"
                  {...field}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            {/* bank account */}
            <Controller
              name="bank_account"
              control={control}
              render={({ field }) => (
                <TextField
                  type="string"
                  fullWidth
                  label={t("bankAccount")}
                  size="small"
                  {...field}
                  error={!!errors.bank_account}
                  helperText={errors.bank_account?.message}
                  InputLabelProps={{
                    shrink: field.value !== undefined,
                  }}
                />
              )}
            />

            {/*  contact number */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={salesT("contactNum")}
                    size="small"
                    {...field}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
              <Controller
                name="configuration.show_phone"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, whiteSpace: "nowrap" }}
                    control={
                      <Checkbox
                        checked={field.value ?? true}
                        onChange={(e) => field.onChange(e.target.checked)}
                        size="small"
                      />
                    }
                    label={salesT("showOnInvoice")}
                  />
                )}
              />
            </Stack>
            {/* email */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="email"
                    fullWidth
                    label={generalT("email")}
                    size="small"
                    {...field}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
              <Controller
                name="configuration.show_email"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, whiteSpace: "nowrap" }}
                    control={
                      <Checkbox
                        checked={field.value ?? true}
                        onChange={(e) => field.onChange(e.target.checked)}
                        size="small"
                      />
                    }
                    label={salesT("showOnInvoice")}
                  />
                )}
              />
            </Stack>
            {/* start balance */}
            <Controller
              name="start_balance"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={salesT("start_balance")}
                  size="small"
                  {...field}
                  error={!!errors.start_balance}
                  helperText={errors.start_balance?.message}
                />
              )}
            />
            {/* Is registered to tax */}
            <FormControl error={!!errors.is_registered_tax}>
              <Box display="flex" alignItems="center" gap={3}>
                <FormLabel>{salesT("isRegisteredToTax")}</FormLabel>

                <Controller
                  name="is_registered_tax"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      row
                      onChange={(event) => {
                        const value = event.target.value;
                        field.onChange(value);
                        // Clear VAT whenever tax registration changes
                        setValue("vat", "");
                      }}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" />}
                        label={generalT("yes")}
                      />

                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" />}
                        label={generalT("no")}
                      />
                    </RadioGroup>
                  )}
                />
              </Box>

              {errors.is_registered_tax && (
                <FormHelperText>
                  {errors.is_registered_tax.message}
                </FormHelperText>
              )}
            </FormControl>
            {/* vat */}
            {watch("is_registered_tax") === "yes" && (
              <Controller
                name="vat"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={generalT("taxNumber")}
                    size="small"
                    {...field}
                    error={!!errors.vat}
                    helperText={errors.vat?.message}
                  />
                )}
              />
            )}
            {/* street */}
            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <TextField
                  required={!!watch("vat")}
                  type="text"
                  fullWidth
                  label={salesT("street")}
                  size="small"
                  {...field}
                  error={!!errors.street}
                  helperText={errors.street?.message}
                />
              )}
            />
            {/* building number */}
            <Controller
              name="building_number"
              control={control}
              render={({ field }) => (
                <TextField
                  required={!!watch("vat")}
                  type="number"
                  fullWidth
                  label={salesT("buildingNum")}
                  size="small"
                  {...field}
                  error={!!errors.building_number}
                  helperText={errors.building_number?.message}
                />
              )}
            />

            <Stack direction="row" alignItems="center" spacing={1}>
              <Controller
                name="country_id"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={countriesData?.data?.countries ?? []}
                    getOptionLabel={(option) => option?.name ?? ""}
                    value={
                      countriesData?.data?.countries?.find(
                        (country) => `${country.id}` === `${field.value}`,
                      ) || null
                    }
                    onChange={(_, value) =>
                      field.onChange(value ? `${value.id}` : "")
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        label={profileT("country")}
                        error={!!errors.country_id}
                        helperText={errors.country_id?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="configuration.show_country_id"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, whiteSpace: "nowrap" }}
                    control={
                      <Checkbox
                        checked={field.value ?? true}
                        onChange={(e) => field.onChange(e.target.checked)}
                        size="small"
                      />
                    }
                    label={salesT("showOnInvoice")}
                  />
                )}
              />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControl fullWidth size="small" error={!!errors.city_id}>
                <InputLabel>{profileT("city")}</InputLabel>

                <Controller
                  name="city_id"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label={profileT("city")}>
                      {citiesData?.data?.cities?.map(({ id, name }) => (
                        <MenuItem key={id} value={`${id}`}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />

                <FormHelperText>{errors.city_id?.message}</FormHelperText>
              </FormControl>
              <Controller
                name="configuration.show_city_id"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, whiteSpace: "nowrap" }}
                    control={
                      <Checkbox
                        checked={field.value ?? true}
                        onChange={(e) => field.onChange(e.target.checked)}
                        size="small"
                      />
                    }
                    label={salesT("showOnInvoice")}
                  />
                )}
              />
            </Stack>

            {/* zip code */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Controller
                name="zip_code"
                control={control}
                render={({ field }) => (
                  <TextField
                    required={!!watch("vat")}
                    type="number"
                    fullWidth
                    label={salesT("zipCode")}
                    size="small"
                    {...field}
                    error={!!errors.zip_code}
                    helperText={errors.zip_code?.message}
                  />
                )}
              />
              <Controller
                name="configuration.show_zip_code"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, whiteSpace: "nowrap" }}
                    control={
                      <Checkbox
                        checked={field.value ?? true}
                        onChange={(e) => field.onChange(e.target.checked)}
                        size="small"
                      />
                    }
                    label={salesT("showOnInvoice")}
                  />
                )}
              />
            </Stack>

            {/* cib */}
            <Controller
              name="cib"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={salesT("cib")}
                  size="small"
                  {...field}
                  error={!!errors.cib}
                  helperText={errors.cib?.message}
                />
              )}
            />

            {/* agreements */}
            <Controller
              name="agreements"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={salesT("agreements")}
                  size="small"
                  {...field}
                  error={!!errors.agreements}
                  helperText={errors.agreements?.message}
                />
              )}
            />
            {/* terms */}
            <FormControl fullWidth size="small" error={!!errors.terms}>
              <InputLabel id="terms-label">{salesT("terms")}</InputLabel>

              <Controller
                name="terms"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    multiple
                    labelId="terms-label"
                    id="terms"
                    label={salesT("terms")}
                    value={field.value || []}
                    sx={{
                      "& .MuiSelect-select": {
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        whiteSpace: "normal",
                        minHeight: "40px",
                      },
                    }}
                    renderValue={(selected) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        {(selected as (string | number)[]).map((id) => {
                          const term = termsData?.data?.find(
                            (term: any) => term.id === id,
                          );

                          return (
                            <Chip key={id} label={term?.title} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {termsData?.data?.map(({ id, title }: any) => (
                      <MenuItem key={id} value={id}>
                        <Checkbox checked={(field.value || []).includes(id)} />
                        <ListItemText primary={title} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />

              <FormHelperText>
                {generalT(`${errors.terms?.message ?? ""}`)}
              </FormHelperText>
            </FormControl>
          </Stack>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {t("cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              fullWidth
              type="submit"
              loading={isPending || isPendingUpdate}
            >
              {t("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddEditClientPopup;
